/**
 * SendFormCard — texted forms for one contact, on the detail right rail.
 * Three jobs (mirrors CallbacksCard's booking-link flow):
 *   1. Text the patient a /f/<token> form link by SMS: POST
 *      /contacts/:id/form-links { form_id, send:true } — same compliance gate
 *      + test-mode override as the composer.
 *   2. Copy the link instead (send:false) for channels we don't automate yet.
 *   3. Show prior links with live status (sent → started → completed) and the
 *      completed answers inline (label-resolved, recruiter-only).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { ClipboardList, Copy, Loader2, Send } from 'lucide-react';
import api from '../../services/api';
import { Contact, ContactFormLink, CrmForm, FormResponseDetail } from '../types';
import { notifyStatsChanged, relTime } from '../lib';
import { answerHighlight, reasonKinds } from './shared';

const errMsg = (e: any): string =>
  e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Request failed';

function blockedReason(reason?: string): string {
  switch (reason) {
    case 'opted_out': return 'contact has opted out of SMS';
    case 'consent_revoked': return 'SMS consent was revoked';
    case 'quiet_hours': return 'outside allowed messaging hours';
    case 'no_phone': return 'contact has no phone number';
    default: return reason || 'blocked';
  }
}

function statusPill(status: string): string {
  switch (status) {
    case 'completed': return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300';
    case 'started': return 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300';
    case 'expired':
    case 'cancelled': return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300';
    default: return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
  }
}

const eligibilityLabel: Record<string, string> = {
  eligible: 'Looks eligible',
  not_eligible: 'Not eligible',
  needs_review: 'Needs review',
};

interface Props {
  contact: Contact;
  /** Bump to refetch (e.g. after the timeline reloads). */
  reloadToken?: number;
  /** Fired after the card sends an SMS so the parent reloads timeline + cadence. */
  onSent?: () => void;
}

export default function SendFormCard({ contact, reloadToken = 0, onSent }: Props) {
  const contactId = contact.id;
  const [links, setLinks] = useState<ContactFormLink[]>([]);
  const [forms, setForms] = useState<CrmForm[]>([]);
  const [formId, setFormId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [copying, setCopying] = useState(false);
  const [openResponse, setOpenResponse] = useState<string | null>(null); // response id
  const [responseDetail, setResponseDetail] = useState<FormResponseDetail | null>(null);
  const [responseLoading, setResponseLoading] = useState(false);
  // Guards a slow fetch from rendering one response's answers under another's
  // header after the recruiter clicks a second "View answers".
  const openResponseRef = useRef<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [linksRes, formsRes] = await Promise.all([
        api.get(`/api/crm/contacts/${contactId}/form-links`),
        api.get('/api/crm/forms', { params: { status: 'active' } }),
      ]);
      setLinks(linksRes.data?.links || []);
      const active = (formsRes.data?.forms || []).filter((f: CrmForm) => f.version > 0);
      setForms(active);
      setFormId((prev) => (prev && active.some((f: CrmForm) => f.id === prev) ? prev : active[0]?.id || ''));
    } catch (e: any) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [contactId]);

  useEffect(() => { load(); }, [load, reloadToken]);

  const sendLink = async () => {
    if (sending || !formId) return;
    setSending(true);
    setActionError(null);
    setNotice(null);
    try {
      const res = await api.post(`/api/crm/contacts/${contactId}/form-links`, { form_id: formId, send: true });
      setNotice(res.data?.sent ? 'Form link texted to the patient.' : 'Form link created.');
      if (res.data?.sent) {
        notifyStatsChanged(); // the send counts as an outreach attempt
        onSent?.();
      }
      await load();
    } catch (e: any) {
      const d = e?.response?.data || {};
      if (e?.response?.status === 409 && d.reason) {
        setActionError(`Link created but SMS not sent: ${blockedReason(d.reason)}.`);
        await load();
      } else {
        const hint = d.hint;
        setActionError(hint ? `${errMsg(e)} — ${hint}` : errMsg(e));
      }
    } finally {
      setSending(false);
    }
  };

  const copyLink = async () => {
    if (copying || !formId) return;
    setCopying(true);
    setActionError(null);
    setNotice(null);
    try {
      const res = await api.post(`/api/crm/contacts/${contactId}/form-links`, { form_id: formId, send: false });
      const url = res.data?.url;
      // Clipboard can reject (permissions, non-secure context) — the link was
      // still minted, so always surface the URL and refresh the list.
      let copied = false;
      if (url && navigator.clipboard) {
        try {
          await navigator.clipboard.writeText(url);
          copied = true;
        } catch { /* fall through to showing the URL */ }
      }
      setNotice(copied ? 'Form link copied to clipboard.' : url ? `Link: ${url}` : 'Form link created.');
      await load();
    } catch (e: any) {
      setActionError(errMsg(e));
    } finally {
      setCopying(false);
    }
  };

  const toggleResponse = async (responseId: string) => {
    if (openResponse === responseId) {
      setOpenResponse(null);
      openResponseRef.current = null;
      setResponseDetail(null);
      return;
    }
    setOpenResponse(responseId);
    openResponseRef.current = responseId;
    setResponseDetail(null);
    setResponseLoading(true);
    try {
      const res = await api.get(`/api/crm/form-responses/${responseId}`);
      if (openResponseRef.current !== responseId) return; // selection moved on
      setResponseDetail(res.data?.response || null);
    } catch (e: any) {
      if (openResponseRef.current !== responseId) return;
      setActionError(errMsg(e));
      setOpenResponse(null);
      openResponseRef.current = null;
    } finally {
      if (openResponseRef.current === responseId) setResponseLoading(false);
    }
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-6">
      <h3 className="mb-3 inline-flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100">
        <ClipboardList className="h-4 w-4 text-primary-600 dark:text-primary-400" />
        Forms
      </h3>

      {notice && (
        <div className="mb-3 rounded-lg border border-primary-200 bg-primary-50 p-2.5 text-xs text-primary-800 dark:border-primary-800 dark:bg-primary-900/20 dark:text-primary-300">
          {notice}
        </div>
      )}
      {(error || actionError) && (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 p-2.5 text-xs text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
          {error || actionError}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
        </div>
      ) : (
        <>
          {forms.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No published forms yet — build one under Forms in the sidebar.
            </p>
          ) : (
            <div className="space-y-2">
              <select
                value={formId}
                onChange={(e) => setFormId(e.target.value)}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                aria-label="Form to send"
              >
                {forms.map((f) => (
                  <option key={f.id} value={f.id}>{f.title}</option>
                ))}
              </select>
              <div className="flex gap-2">
                <button
                  onClick={sendLink}
                  disabled={sending || copying || !formId || !contact.phone_e164 || contact.sms_opt_out}
                  title={contact.sms_opt_out ? 'Patient opted out of SMS'
                    : !contact.phone_e164 ? 'No phone number on file'
                    : 'Text the patient a link to fill this form on their phone'}
                  className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-primary-600 px-3 py-2 text-xs font-medium text-white transition-colors duration-150 hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50 min-h-[44px] sm:min-h-0"
                >
                  {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                  {sending ? 'Sending…' : 'Text form link'}
                </button>
                <button
                  onClick={copyLink}
                  disabled={sending || copying || !formId}
                  title="Create the link and copy it to the clipboard"
                  className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-xs font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 min-h-[44px] sm:min-h-0"
                >
                  {copying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Copy className="h-3.5 w-3.5" />}
                  Copy
                </button>
              </div>
            </div>
          )}

          {links.length > 0 && (
            <div className="mt-4 divide-y divide-gray-100 dark:divide-gray-700">
              {links.map((l) => (
                <div key={l.id} className="py-2.5">
                  <div className="flex items-center justify-between gap-3">
                    <span className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{l.form_title}</span>
                    <span className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium ${statusPill(l.status)}`}>
                      {l.status}
                    </span>
                  </div>
                  <p className="mt-0.5 text-xs text-gray-400">
                    {l.status === 'completed' && l.response?.completed_at
                      ? `Completed ${relTime(l.response.completed_at)}`
                      : l.sent_at
                      ? `Sent ${relTime(l.sent_at)}`
                      : `Created ${relTime(l.created_at)}`}
                    {l.response?.eligibility_status && (
                      <span className="ml-1.5 font-medium text-gray-500 dark:text-gray-300">
                        · {eligibilityLabel[l.response.eligibility_status] || l.response.eligibility_status}
                      </span>
                    )}
                  </p>
                  {l.response?.status === 'completed' && (
                    <button
                      onClick={() => toggleResponse(l.response!.id)}
                      className="mt-1 text-xs font-medium text-primary-700 hover:underline dark:text-primary-400"
                    >
                      {openResponse === l.response.id ? 'Hide answers' : 'View answers'}
                    </button>
                  )}
                  {openResponse === l.response?.id && (
                    <div className="mt-2 rounded-lg bg-gray-50 p-3 dark:bg-gray-900/50">
                      {responseLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                      ) : responseDetail ? (
                        <dl className="space-y-2">
                          {/* Red = met an exclusion rule, yellow = met a needs-review
                              rule (the response's stored eligibility_reasons) — same
                              treatment as FormResponseModal / ScreenerPanel. */}
                          {(() => {
                            const kinds = reasonKinds(responseDetail.eligibility_reasons);
                            return responseDetail.answers.map((a) => {
                              const kind = kinds.get(a.question_key);
                              return (
                                <div key={a.question_key}>
                                  <dt className="text-xs text-gray-500 dark:text-gray-400">{a.label}</dt>
                                  <dd className="text-sm">
                                    <span
                                      className={`break-words ${answerHighlight(kind)}`}
                                      title={kind === 'exclude' ? 'Meets exclusion criteria' : kind === 'review' ? 'Needs review' : undefined}
                                    >
                                      {a.display}
                                    </span>
                                  </dd>
                                </div>
                              );
                            });
                          })()}
                          {responseDetail.answers.length === 0 && (
                            <p className="text-xs text-gray-400">No answers recorded.</p>
                          )}
                        </dl>
                      ) : (
                        <p className="text-xs text-gray-400">Could not load answers.</p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
